import { Box } from '@capitalise/ui-design-system';
import { ListCompanyItem, SearchClientTitle } from '../../components';
import { createStructuredSelector } from 'reselect';
import * as monitorActions from 'reducers/monitor/reducer';
import { makeFilteredMonitorAlphaClientsListByStatus } from '../../../../reducers/monitor/overview/selectors';
import { connect } from 'react-redux';
import { makeSelectUserCompanyId } from '../../../../reducers/user/selectors';
import React, { FC, useEffect } from 'react';
import { useSearchClientContext } from '../../hooks/useSearchClientContext';
import { List } from 'immutable';

export const ARCHIVE_CLIENTS_TITLE = 'Existing unassigned clients you want to keep';
export const ARCHIVE_CLIENTS_SUBTITLE =
  'List of your existing clients that you would like to keep.';

export interface ArchiveExistingClientsProps {
  clients: List<{ companyName: string; entityId: string; size: number }>;
  userCompanyId: number;
  requestClients: (entityId: number) => void;
  disabled: boolean;
}

export const ArchiveExistingClients: FC<ArchiveExistingClientsProps> = ({
  clients,
  userCompanyId,
  requestClients,
  disabled,
}: ArchiveExistingClientsProps) => {
  const { existingCompanies, onCompanyArchive, setExistingCompanies } = useSearchClientContext();

  useEffect(() => {
    requestClients(userCompanyId);
  }, []);

  useEffect(() => {
    if (clients?.size && !disabled) {
      setExistingCompanies(clients?.toArray());
    }
  }, [clients]);

  const checkIfCompanyDissolved = (company) => company?.preview?.isCompanyDissolved;

  const ChipForDissolvedCompany = () => (
    <Box
      backgroundColor="danger-50"
      borderRadius="100px"
      color="danger-500"
      display="inline"
      height="xsmall"
      ml="tiny"
      px="tiny"
      py="xtiny"
      typography="caption-12"
      width={68}
    >
      Dissolved
    </Box>
  );

  const sortByDissolving = (companiesList) =>
    companiesList.reduce(
      (acc, company) => (checkIfCompanyDissolved(company) ? [company, ...acc] : [...acc, company]),
      [],
    );

  if (!existingCompanies.length) {
    return null;
  }

  return (
    <Box>
      <SearchClientTitle
        title={ARCHIVE_CLIENTS_TITLE}
        subTitle={ARCHIVE_CLIENTS_SUBTITLE}
        clientsCount={existingCompanies.length}
      />
      <Box marginTop="3xsmall" overflowY="scroll">
        {sortByDissolving(existingCompanies)?.map((selectedCompany) => (
          <ListCompanyItem
            buttonText="Archive"
            company={selectedCompany}
            chip={checkIfCompanyDissolved(selectedCompany) ? <ChipForDissolvedCompany /> : false}
            onButtonClick={onCompanyArchive}
            key={selectedCompany.entityId}
            disabled={disabled}
          />
        ))}
      </Box>
    </Box>
  );
};

const mapStateToProps = createStructuredSelector({
  clients: makeFilteredMonitorAlphaClientsListByStatus(),
  userCompanyId: makeSelectUserCompanyId(),
});

const mapDispatchToProps = (dispatch) => ({
  requestClients: (entityId, archived) =>
    dispatch(monitorActions.requestAllClients(entityId, archived)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ArchiveExistingClients);
